/**
 * Tiny resilient localStorage cache for API responses.
 *
 * Why: production deployments occasionally take ~20 minutes to reconnect to
 * the database after a pod restart, during which time `/api/settings` and
 * `/api/projects` return empty/erroring responses. Without a cache, visitors
 * see a blank page (just logo + categories) until the backend recovers.
 *
 * With this cache, every successful API response for a "shell" key is
 * persisted to localStorage. On the next page load, components hydrate
 * INSTANTLY from the cached snapshot before the live API call completes.
 * If the live call later returns empty/error, we KEEP the cached snapshot
 * on screen instead of replacing it with a blank state.
 *
 * The cache is intentionally small (just shell content), versioned, and
 * expires after a generous TTL so stale data doesn't linger forever.
 */

const PREFIX = "bda_cache_v1:";
// Settings (logo, bio, contact info) change rarely → long TTL is OK.
// Projects change frequently → short TTL prevents stale cross-device views.
// Per-key TTL allows different freshness budgets per data type.
const TTL_SETTINGS_MS = 1000 * 60 * 60 * 24 * 7; // 7 days
const TTL_PROJECTS_MS = 1000 * 60 * 2; // 2 minutes — cross-device cache won't drift more than this
const TTL_DEFAULT_MS = TTL_PROJECTS_MS;

function ttlFor(key) {
  if (key && key.startsWith("settings")) return TTL_SETTINGS_MS;
  if (key && key.startsWith("projects:")) return TTL_PROJECTS_MS;
  return TTL_DEFAULT_MS;
}

export function readCache(key) {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (Date.now() - (parsed.t || 0) > ttlFor(key)) return null;
    return parsed.v;
  } catch {
    return null;
  }
}

export function writeCache(key, value) {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify({ t: Date.now(), v: value }));
  } catch {
    // localStorage full or disabled — silently no-op
  }
}

/**
 * Invalidate all cache entries whose key starts with the given namespace.
 * Used after any project mutation (create, update, delete, slot upload) so
 * the admin grid and the public landing page always converge to the same
 * fresh data on next render — eliminates the "admin grid doesn't match
 * frontend" divergence caused by stale localStorage snapshots.
 */
export function invalidateCache(namespace) {
  try {
    const fullPrefix = PREFIX + namespace;
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(fullPrefix)) keysToRemove.push(k);
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

/**
 * Decide whether a fresh API response is "good enough" to overwrite the
 * cache. We treat empty arrays, null, and undefined as suspicious during
 * a backend hiccup: keep the cached snapshot instead of nuking content.
 */
export function isMeaningfulResponse(value) {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") {
    if (Array.isArray(value.items)) return true; // list endpoint with metadata is fine even if empty
    return Object.keys(value).length > 0;
  }
  return true;
}
